import dayjs from "dayjs";
import { isEmpty } from "lodash";
import { APPOINTMENT_OFFSET, TENTATIVE_FALLBACK_TIME } from "../library/constants";
import { DATE_TIME_FORMATS } from "../utils/dateTimes";

/**
 * Build a local "YYYY-MM-DD HH:mm:ss" date-time string for an appointment.
 * @param {dayjs.Dayjs|string|Date} appointmentDate - The selected date.
 * @param {string} appointmentTime - The selected time in "h:mm A" format (e.g. "10:30 AM").
 * @param {boolean} isTentative - Whether the appointment is tentative (uses fallback time).
 * @returns {string} e.g. "2026-04-14 10:30:00"
 */
export const buildAppointmentStartDate = (appointmentDate, appointmentTime, isTentative) => {
  const dateStr = dayjs(appointmentDate).format(DATE_TIME_FORMATS.YYYY_MM_DD);
  const timeStr = dayjs(
    isTentative ? TENTATIVE_FALLBACK_TIME : appointmentTime,
    DATE_TIME_FORMATS.h_mm_A
  ).format(DATE_TIME_FORMATS.HH_MM_SS);
  return `${dateStr} ${timeStr}`;
};

/**
 * Derive the [startTime, endTime] selectable window (formatted as "h:mm A") for a
 * given calendar date based on the company's configured working hours and an optional
 * per-location offset override. Returns `[]` when the date is missing, the day has no
 * working-hours record, or computation fails.
 *
 * Mirrors the inline `workHoursTime` logic previously duplicated in appointmentForm.js
 * and TaskDrawer.jsx so any future caller (other forms / drawers / pickers) can reuse it.
 *
 * @param {dayjs.Dayjs|string|Date} date - The day to compute the window for.
 * @param {Array} worksHours - Company working-hours rows: [{ weekDay, startTime, endTime, ... }, ...]
 * @param {Object} [locationOffsetSettings] - Optional `APPOINTMENT_CREATE_OFFSET` override from the location settings.
 * @returns {string[]} `[start, end]` in "h:mm A" format, or `[]` if not derivable.
 */
/**
 * Returns true when the given calendar date falls on a weekday flagged as a non-working
 * day in the company's working-hours configuration (i.e. the store is closed that day).
 * Designed to be composed inside a DatePicker's `disabledDate` callback alongside
 * past-date checks. Mirrors the inline `disableWeekDay` logic in appointmentForm.js.
 *
 * @param {dayjs.Dayjs|string|Date} date - The date to evaluate.
 * @param {Array} worksHours - Company working-hours rows: [{ weekDay, isWorkingDay, ... }, ...]
 * @returns {boolean} `true` if the weekday is closed; `false` otherwise (and when worksHours is missing).
 */
export const isNonWorkingDay = (date, worksHours) => {
  if (!date) return false;
  const day = dayjs(date).format(DATE_TIME_FORMATS.DDDD).toUpperCase();
  return (worksHours || []).some(el => el?.weekDay === day && !el?.isWorkingDay);
};

export const calculateWorkingHoursWindow = (date, worksHours, locationOffsetSettings) => {
  try {
    if (!date) return [];
    const day = dayjs(date).format(DATE_TIME_FORMATS.DDDD);
    const workDay = (worksHours || []).find(el => el?.weekDay === day.toUpperCase());
    if (!workDay) return [];

    const offset = !isEmpty(locationOffsetSettings) ? locationOffsetSettings : APPOINTMENT_OFFSET;
    const afterOpenOffset = offset.AFTER_OPEN ?? APPOINTMENT_OFFSET.AFTER_OPEN;
    const beforeCloseOffset = offset.BEFORE_CLOSE ?? APPOINTMENT_OFFSET.BEFORE_CLOSE;

    const hours = [];
    const startHrs =
      workDay.startTime &&
      dayjs(workDay.startTime, DATE_TIME_FORMATS.HH_MM_SS)
        .add(afterOpenOffset, "minute")
        .format(DATE_TIME_FORMATS.h_mm_A);
    if (startHrs) hours.push(startHrs);
    const endHrs =
      workDay.endTime &&
      dayjs(workDay.endTime, DATE_TIME_FORMATS.HH_MM_SS)
        .subtract(beforeCloseOffset, "minute")
        .format(DATE_TIME_FORMATS.h_mm_A);
    if (endHrs) hours.push(endHrs);
    return hours;
  } catch (error) {
    console.error("Error calculating work hours window:", error);
    return [];
  }
};
